<template>
  <div>
    <page-header
      :actions="headerActions"
      left-side-class="col-xl-8 pb-2"
      :page="headerPage"
      right-side-class="col-xl align-items-end d-flex pb-4 justify-content-end"
    />
    <div class="info-card mb-4">
      <div class="info-card__header">
        <div class="py-1 px-2 bg-white rounded-circle mr-2 shadow-sm">
          <i class="fas fa-book-user text-orange" />
        </div>
        {{ model.type?.name | empty }}
      </div>

      <div v-if="model.type?.description" class="info-card__body">
        {{ model.type?.description | empty }}
      </div>
    </div>

    <validation-observer ref="instrumentObserver">
      <structured-interview
        v-if="isStructuredInterview"
        v-model="model.structuredInterview"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
      <disability-evaluation-questionnaire
        v-else-if="isDisabilityEvaluationQuestionnaire"
        v-model="model.disabilityEvaluationQuestionnaire"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
      <interest-profile
        v-else-if="isInterestProfile"
        v-model="model.interestProfile"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
      <learning-styles-relevant-to-performance
        v-else-if="isLearningStylesRelevantToPerformance"
        v-model="model.learningStylesRelevantToPerformance"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
      <work-sample-execution
        v-else-if="isWorkSampleExecution"
        v-model="model.workSampleExecution"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
      <valpar-system-sample
        v-else-if="isValparSystem"
        v-model="model.valparSystemSample"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />

      <samr-oral-instruction-screen
        v-else-if="isSAMPR"
        v-model="model.samrOralInstructionScreen"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
      <basic-skills-test
        v-else-if="isBasicSkillsTest"
        v-model="model.academicSkillsTest"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
      <other-academic-tests
        v-else-if="isOtherAcademicTests"
        v-model="model.otherAcademicTests"
        :is-read-only="isReadOnly"
        @update:initialModel="setInitialModel()"
      />
    </validation-observer>
    <div v-if="!isReadOnly" class="d-flex justify-content-end">
      <action-button
        i="fas fa-save"
        text="Guardar"
        variant="success pl-3 pr-3 my-3"
        @click="save()"
      />
    </div>
  </div>
</template>

<script>
import {
  getInstrument,
  saveInstrument,
  changeInstrumentStatus,
} from '@/modules/vocational-evaluation/api.js';

import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';

import StructuredInterview from './structuredInterview/Main.vue';
import DisabilityEvaluationQuestionnaire from './disabilityEvaluationQuestionnaire/Main.vue';
import InterestProfile from './interestProfile/Main.vue';
import LearningStylesRelevantToPerformance from './learningStylesRelevantToPerformance/Main.vue';
import WorkSampleExecution from './workSampleExecution/Main.vue';
import SamrOralInstructionScreen from './SAMROralInstructionScreen/Main.vue';
import ValparSystemSample from './VALPARSystemSample/Main.vue';
import BasicSkillsTest from './basicSkillsTest/Main.vue';
import OtherAcademicTests from './otherAcademicTests/Main.vue';

import Instruments from '@/utils/constants/vocationalEvaluationInstrumentEnum';
import InstrumentStatusTypes from '@/utils/constants/instrumentStatuses.js';

import { copy, equals } from '@/utils/objUtils';

export default {
  name: 'InstrumentDetail',
  components: {
    PageHeader,
    ActionButton,
    StructuredInterview,
    DisabilityEvaluationQuestionnaire,
    InterestProfile,
    LearningStylesRelevantToPerformance,
    WorkSampleExecution,
    SamrOralInstructionScreen,
    ValparSystemSample,
    BasicSkillsTest,
    OtherAcademicTests,
  },
  mixins: [storeMixin, alertMixin],
  data() {
    return {
      model: {
        type: {},
        structuredInterview: {},
        disabilityEvaluationQuestionnaire: {},
        interestProfile: {
          alternativeGoals: [],
          goals: [],
        },
        learningStylesRelevantToPerformance: {},
        workSampleExecution: {},
        valparSystemSample: {},
        samrOralInstructionScreen: {},
        academicSkillsTest: {},
        otherAcademicTests: {},
      },
      initialModel: {},
    };
  },
  computed: {
    headerPage() {
      return {
        title: this.model.type?.name || '',
        icon: 'fas fa-file-invoice',
        routes: [
          'Inicio',
          'Proveedor',
          'Evaluación Vocacional',
          this.model.type?.name || '',
        ],
      };
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.back(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          visible: !this.isReadOnly,
          click: () => this.save(),
        },
        {
          text: 'Completar',
          icon: 'fas fa-flag',
          variant: 'success',
          visible: !this.isReadOnly,
          click: () => this.save(true),
        },
        {
          text: 'Regresar a Borrador',
          icon: 'fas fa-reply-all',
          variant: 'success',
          visible: this.canRollbackToDraft,
          click: () => this.rollbackToDraft(),
        },
      ];
    },
    id() {
      return this.$route.params.id;
    },
    instrumentTypeId() {
      return Number(this.model.type?.id);
    },
    isReadOnly() {
      return this.model.status?.id != InstrumentStatusTypes.Draft;
    },
    canRollbackToDraft() {
      return !this.model.isEvaluationSubmitted && this.isReadOnly;
    },
    hasChanges() {
      if (this.isReadOnly) return false;
      return !equals(this.model, this.initialModel);
    },
    isStructuredInterview() {
      return this.instrumentTypeId === Instruments.StructuredInterview;
    },
    isDisabilityEvaluationQuestionnaire() {
      return (
        this.instrumentTypeId === Instruments.DisabilityEvaluationQuestionnaire
      );
    },
    isInterestProfile() {
      return this.instrumentTypeId === Instruments.InterestProfile;
    },
    isLearningStylesRelevantToPerformance() {
      return this.instrumentTypeId === Instruments.LearningStyles;
    },
    isWorkSampleExecution() {
      return this.instrumentTypeId === Instruments.WorkSampleExecution;
    },
    isSAMPR() {
      return this.instrumentTypeId === Instruments.SAMPR;
    },
    isValparSystem() {
      return this.instrumentTypeId === Instruments.ValparSystem;
    },
    isBasicSkillsTest() {
      return this.instrumentTypeId === Instruments.BasicSkillsTest;
    },
    isOtherAcademicTests() {
      return this.instrumentTypeId === Instruments.OtherTests;
    },
  },
  async mounted() {
    await this.get();
  },
  methods: {
    setInitialModel() {
      this.initialModel = copy(this.model);
    },
    async get() {
      if (!this.id) return;
      try {
        const { data } = await getInstrument(this.id);
        this.model = data || {};

        await this.$nextTick();

        this.setInitialModel();
      } catch (error) {
        this.showError('Ocurrió un error al cargar la información');
      }
    },

    async save(completed = false) {
      if (completed) {
        const isValid = await this.$refs.instrumentObserver.validate();
        if (!isValid) {
          this.ShowToast(
            'Atención',
            'Por favor completa los campos requeridos antes de completar el instrumento.',
            'error'
          );
          return;
        }
      }
      try {
        const payload = {
          ...this.model,
        };
        if (this.isInterestProfile) {
          payload.interestProfile.inventories = this.model.interestProfile.inventories.filter(
            (i) => i.checked
          );
        }
        await saveInstrument(this.id, {
          ...payload,
          completed,
        });
        this.ShowToast(
          'Exito',
          'La información fue guardada correctamente.',
          'success'
        );
        this.$refs.instrumentObserver.reset();
        await this.get();
      } catch (error) {
        this.ShowToast(
          'Atención',
          'Ocurrió un error al guardar la información.',
          'error'
        );
      }
    },
    async rollbackToDraft() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea regresar el instrumento a borrador?',
        'Sí',
        'No'
      );

      if (!confirmed) return;

      await changeInstrumentStatus(this.id)
        .then(() => {
          this.ShowToast(
            'Proceso exitoso',
            'Los cambios fueron guardados exitosamente',
            'success'
          );
        })
        .catch((e) => this.ShowToast('', e?.response?.data?.message, 'error'));
      this.$refs.instrumentObserver.reset();
      await this.get();
    },
    async confirm() {
      const { value: confirmed, dismiss } = await this.ShowCancelModalClosable(
        'Confirmación',
        '<div>Cuenta con cambios sin guardar,</div><div>¿Desea guardar la información previamente documentada?</div>'
      );
      const cancelled = ['close', 'backdrop'].includes(dismiss);
      if (cancelled) return { cancelled, confirmed: false };
      if (confirmed) await this.save();
      return { confirmed, cancelled };
    },
    async back() {
      if (this.hasChanges) {
        const { cancelled } = await this.confirm();
        if (cancelled) return;
      }
      this.$router.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.info-card {
  background: #f7f7f7;
  border-radius: 6px;
  padding: 1rem;
}

.info-card__header {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.info-card__body {
  background: #fff;
  border-radius: 4px;
  padding: 0.75rem;
  line-height: 1.35;
}
</style>
